import { useCallback, useEffect, useState } from 'react';
import { Logger } from 'utilities';

export interface UseExcelExportDataLoadingProps {
  isOpen: boolean;
  onDataLoaded?: (data: any) => void;
  onError?: (error: string) => void;
}

export const useExcelExportDataLoading = ({ 
  isOpen, 
  onDataLoaded, 
  onError 
}: UseExcelExportDataLoadingProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [dataLoadError, setDataLoadError] = useState<string | null>(null);

  const startLoading = useCallback(() => {
    setIsLoading(true);
    setDataLoadError(null);
  }, []);

  const stopLoading = useCallback(() => {
    setIsLoading(false);
  }, []);

  const setError = useCallback((error: string) => {
    setDataLoadError(error);
    onError?.(error);
  }, [onError]);

  const clearError = useCallback(() => {
    setDataLoadError(null);
  }, []);

  const loadData = useCallback(async (loadFunction: () => Promise<any>) => {
    if (!isOpen) {
      return;
    }

    startLoading();
    try {
      const data = await loadFunction();
      onDataLoaded?.(data);
    } catch (error) {
      Logger.error('Error loading data', error);
      setError('Failed to load data');
    } finally {
      stopLoading();
    }
  }, [isOpen, startLoading, stopLoading, setError, onDataLoaded]);

  useEffect(() => {
    if (!isOpen) {
      clearError();
    }
  }, [isOpen, clearError]);

  return {
    isLoading,
    dataLoadError,
    startLoading,
    stopLoading,
    setError,
    clearError,
    loadData,
  };
};
